import React, { useState, useEffect, useRef, useCallback } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  Button,
  ButtonGroup,
  Skeleton,
  Snackbar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkIcon from '@mui/icons-material/Link';
import Layout from '../components/Layout';
import { useUnion } from '../contexts/UnionContext';
import adminService from '../services/adminService';

function Transactions() {
  const { t } = useTranslation();
  usePageTitle(t('transactions.title'));
  const { activeUnion, isUnionAdmin } = useUnion();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [direction, setDirection] = useState(''); // '' | 'CREDIT' | 'DEBIT'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [importing, setImporting] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const fileInputRef = useRef(null);

  const fetchTransactions = useCallback(() => {
    if (!activeUnion) { setLoading(false); return; }
    setLoading(true);
    const params = {};
    if (direction) params.direction = direction;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    adminService.getTransactions(params)
      .then(data => {
        setTransactions(Array.isArray(data) ? data : data.results || []);
        setError('');
      })
      .catch(() => setError(t('transactions.failedToLoad')))
      .finally(() => setLoading(false));
  }, [activeUnion, direction, startDate, endDate, t]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleCsvImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImporting(true);
    try {
      const result = await adminService.importTransactionsCsv(file);
      setSnackbar({ severity: 'success', message: t('transactions.imported', { created: result.created, skipped: result.skipped }) });
      fetchTransactions();
    } catch (err) {
      const msg = err.response?.data?.error || t('transactions.importFailed');
      setSnackbar({ severity: 'error', message: msg });
    } finally {
      setImporting(false);
    }
  };

  const filtered = transactions.filter(tx => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (tx.counterparty_name || '').toLowerCase().includes(q) ||
      (tx.description || '').toLowerCase().includes(q) ||
      (tx.reference || '').toLowerCase().includes(q) ||
      String(tx.amount).includes(q)
    );
  });

  // Totals
  const totalCredit = filtered.filter(t => t.direction === 'CREDIT').reduce((s, t) => s + parseFloat(t.amount), 0);
  const totalDebit = filtered.filter(t => t.direction === 'DEBIT').reduce((s, t) => s + parseFloat(t.amount), 0);

  if (!activeUnion) {
    return (
      <Layout>
        <Box sx={{ py: 4 }}>
          <Alert severity="warning">{t('transactions.selectUnion')}</Alert>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ py: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4">{t('transactions.title')}</Typography>
          {isUnionAdmin && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={handleCsvImport}
              />
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
              >
                {importing ? t('common.importing') : t('transactions.importCsv')}
              </Button>
            </>
          )}
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder={t('transactions.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ width: 240 }}
          />
          <ButtonGroup size="small" variant="outlined">
            <Button
              variant={direction === '' ? 'contained' : 'outlined'}
              onClick={() => setDirection('')}
            >{t('transactions.all')}</Button>
            <Button
              variant={direction === 'CREDIT' ? 'contained' : 'outlined'}
              color="success"
              onClick={() => setDirection('CREDIT')}
            >{t('transactions.incoming')}</Button>
            <Button
              variant={direction === 'DEBIT' ? 'contained' : 'outlined'}
              color="error"
              onClick={() => setDirection('DEBIT')}
            >{t('transactions.outgoing')}</Button>
          </ButtonGroup>
          <TextField
            size="small"
            label={t('transactions.from')}
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
          />
          <TextField
            size="small"
            label={t('transactions.to')}
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
          />
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Totals summary */}
        {!loading && filtered.length > 0 && (
          <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
            <Typography variant="body2" color="success.main">
              + {totalCredit.toFixed(2)} €
            </Typography>
            <Typography variant="body2" color="error.main">
              − {totalDebit.toFixed(2)} €
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('transactions.net')}: {(totalCredit - totalDebit).toFixed(2)} €
            </Typography>
          </Box>
        )}

        {loading ? (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[t('transactions.date'), t('transactions.type'), `${t('sentInvoices.amount')}`, t('transactions.counterparty'), t('transactions.description'), t('transactions.reference'), t('apartments.invoice')].map(h => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <TableCell key={j}><Skeleton variant="text" /></TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : filtered.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {transactions.length === 0
                ? t('transactions.noTransactions')
                : t('transactions.noResults')}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('transactions.date')}</TableCell>
                  <TableCell>{t('transactions.type')}</TableCell>
                  <TableCell align="right">{t('sentInvoices.amount')}</TableCell>
                  <TableCell>{t('transactions.counterparty')}</TableCell>
                  <TableCell>{t('transactions.description')}</TableCell>
                  <TableCell>{t('transactions.reference')}</TableCell>
                  <TableCell>{t('apartments.invoice')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(tx => (
                  <TableRow key={tx.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                        {tx.transaction_date}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tx.direction === 'CREDIT' ? t('transactions.in') : t('transactions.out')}
                        size="small"
                        color={tx.direction === 'CREDIT' ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        color={tx.direction === 'CREDIT' ? 'success.main' : 'error.main'}
                        fontWeight="medium"
                      >
                        {tx.direction === 'CREDIT' ? '+' : '−'}{parseFloat(tx.amount).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{tx.counterparty_name || '—'}</Typography>
                      {tx.counterparty_account && (
                        <Typography variant="caption" color="text.secondary">{tx.counterparty_account}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {tx.description || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {tx.reference || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {tx.linked_invoice_display ? (
                        <Tooltip title={`Apt ${tx.linked_invoice_display.apartment_number} — ${tx.linked_invoice_display.period_year}/${String(tx.linked_invoice_display.period_month).padStart(2, '0')}`}>
                          <Chip
                            icon={<LinkIcon />}
                            label={`${tx.linked_invoice_display.period_year}/${String(tx.linked_invoice_display.period_month).padStart(2, '0')}`}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar && (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        )}
      </Snackbar>
    </Layout>
  );
}

export default Transactions;
